/**
 * API Hooks for Users
 */
import { useQuery } from '@tanstack/react-query';
import { getApiClient } from '@/lib/api-client';
import { useAuth, useAuthTokenReady } from './use-auth';

export interface AdminUser {
  id: string;
  email: string | null;
  created_at: string | null;
  last_sign_in_at: string | null;
  role: 'seeker' | 'partner' | 'admin' | null;
  status: 'active' | 'blocked' | null;
}

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: () => [...userKeys.lists()] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
};

export function useUsers() {
  const apiClient = getApiClient();
  const { user, loading } = useAuth();
  const tokenReady = useAuthTokenReady();
  
  return useQuery<AdminUser[]>({
    queryKey: userKeys.list(),
    queryFn: async () => {
      const response = await apiClient.request<{ users: AdminUser[] }>('/admin/users');
      return response.users;
    },
    enabled: !loading && !!user && tokenReady, // Only fetch when authenticated and token is ready
  });
}

export function useUser(id: string) {
  return useQuery<AdminUser>({
    queryKey: userKeys.detail(id),
    queryFn: async () => {
      // TODO: Implement user detail endpoint in API
      throw new Error('Not implemented');
    },
    enabled: !!id,
  });
}

